import { action, computed, makeAutoObservable } from "mobx";

import {Image, ImageRef, UserImage} from '../types';

export class ImageStore {
  private images = new Map<string, Image>();
  private maxSelection = new Map<string, number>();
  private countSelection = new Map<string, number>();

  constructor() {
    makeAutoObservable(this);
  }

  @action
  addAllImages(images: ImageRef[]) {
    for (const image of images) {
      if (!this.has(image) || this.shouldUpdate(image)) {
        this.add(image);
      }
    }
  }

  @action
  add(image: ImageRef) {
    // If overwriting an image, preserve its select/rank to avoid
    // making it flash/rerender
    let oldImage = this.images.get(image.src);
    let selectionRank = 0;
    let isSelected = false;

    if (oldImage) {
      selectionRank = oldImage.selectionRank;
      isSelected = oldImage.isSelected;
    }

    // We add a full structure that the grid/gallery needs
    this.images.set(image.src, {
      image_id: image.image_id,
      src: image.src,
      thumbnail: image.small_img_src,
      thumbnailWidth: 256,
      thumbnailHeight: 256,
      isSelected: isSelected,
      isWinner: image.winner,
      votes: image.votes,
      round: image.rnd,
      enableImageSelection: true,
      thumbnailCaption: image.image_id,
      selectionRank: selectionRank,
      openseaLink: image.opensea_link ?? "",
    });
  }

  @action
  updateUserImages(userImages: UserImage[]) {
    for (let image of userImages) {
      let inStore = this.getImage(image.src);
      if (inStore) {
        const image_copy = { ...inStore };  // cannot modify image directly, need to make copy
        image_copy.selectionRank = image.rank + 1;
        image_copy.isSelected = true;

        if (this.shouldUpdateImg(image_copy)) {
          this.addImg(image_copy);
        }
      }
    }
  }

  @action
  addImg(image: Image) {
    // We add a full structure that the grid/gallery needs
    this.images.set(image.src, image);
  }

  @action
  setMaxSelection(round: string, limit: number) {
    this.maxSelection.set(round, limit);
  }

  @computed
  getMaxSelection(round: string) {
    return this.maxSelection.get(round);
  }

  @computed
  has(image: ImageRef): boolean {
    return this.images.has(image.src);
  }

  @computed
  shouldUpdate(image: ImageRef): boolean {
    let img = this.images.get(image.src);

    if (
      img &&
      (img.votes !== image.votes || img.openseaLink !== image.opensea_link)
    ) {
      return true;
    }
    return false;
  }

  @computed
  shouldUpdateImg(image: Image): boolean {
    let img = this.images.get(image.src);

    if (
      img &&
      (img.votes !== image.votes ||
        img.selectionRank !== image.selectionRank ||
        img.openseaLink !== image.openseaLink)
    ) {
      return true;
    }
    return false;
  }

  @action
  clear() {
    this.images.clear();
  }

  @computed
  getImages(round: string): Image[] {
    return [...this.images.values()].filter((e) => e.round === round);
  }

  @computed
  hasImages(round: string): boolean {
    return this.getImages(round).length > 0;
  }

  @action
  selectImage(image: string, checked: boolean, newRank: number) {
    try {
      let toSelect = this.images.get(image);

      if (toSelect) {
        toSelect.selectionRank = newRank;
        toSelect.isSelected = checked;

        // Now find any images with this same rank and de rank/select them
        // since this could have been dropping an image onto another on the voting bar
        for (let [key, img] of this.images) {
          if (img.round !== toSelect.round) {
            continue;
          }

          if (img.selectionRank === newRank && img !== toSelect) {
            img.selectionRank = 0;
            img.isSelected = false;
            this.images.set(key, img);
          }
        }
      }
    } catch (error) {
      console.log("Failed to select: ", error);
      return false;
    }
  }

  @action
  swapRank(image1: string, image2: string) {
    // Swap the rankings of two images which should both be selected (dropping onto each other on ranking bar)
    if (image1 === image2) {
      return;
    }

    try {
      let image1Ref = this.images.get(image1);
      let image2Ref = this.images.get(image2);

      if (image1Ref && image2Ref) {

        let oldRank     = image1Ref.selectionRank;
        let oldSelected = image1Ref.isSelected;

        image1Ref.selectionRank = image2Ref.selectionRank;
        image1Ref.isSelected    = image2Ref.isSelected;

        image2Ref.selectionRank = oldRank;
        image2Ref.isSelected    = oldSelected;

        this.images.set(image1, image1Ref);
        this.images.set(image2, image2Ref);
      } else {
        console.log("Failed to swap images! ", image1, image2);
      }
    } catch (error) {
      console.log("Failed to select: ", error);
      return false;
    }
  }

  @computed
  getSelection(round: string): Image[] {
    return [
      ...this.getImages(round).filter((value) => {
        return value.isSelected;
      }),
    ].sort((a, b) => a.selectionRank - b.selectionRank);
  }

  @computed
  getSelectionRanked(round: string, rank: number): Image[] {
    return [
      ...this.getImages(round).filter((value) => {
        return value.isSelected && value.selectionRank === rank;
      }),
    ];
  }

  @computed
  allImageSelected(round: string): boolean {
    const max = this.maxSelection.get(round) || 0;
    return this.getSelection(round).length === max;
  }

  @computed
  getImage(src: string): Image | undefined {
    return this.images.get(src);
  }
}
